import { useCallback, useEffect, useMemo, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Admin advisor + pool management dashboard.
 *
 * Mounted at /admin/advisors. Authenticated via the portal's Supabase
 * session (cross-subdomain cookie); requests to /api/admin/advisors
 * (GET) and /api/admin/advisor-action (POST) carry the access token
 * through authedFetch as Authorization: Bearer.
 *
 * Each action refetches the full roster after completing — simpler than
 * partial state reconciliation and the request is cheap (3 small queries).
 */

interface PoolView {
  id: string;
  slug: string;
  displayName: string;
  isActive: boolean;
  defaultLeadTimeMinutes: number;
}

interface MembershipView {
  poolId: string;
  poolSlug: string;
  currentLevel: number;
  weight: number;
  creditSkipAdjustment: number;
  isPausedInPool: boolean;
  meetingsCountTotal: number;
  meetingsCountThisMonth: number;
}

interface AdvisorView {
  id: string;
  fullName: string;
  email: string;
  isActive: boolean;
  notes: string | null;
  receivesYesLeads: boolean;
  receivesLeads: boolean;
  memberships: MembershipView[];
}

interface AdvisorsApiResponse {
  ok: boolean;
  pools?: PoolView[];
  advisors?: AdvisorView[];
  error?: string;
  message?: string;
}

interface ActionApiResponse {
  ok: boolean;
  error?: string;
  message?: string;
}

export function AdminAdvisors() {
  const auth = useCurrentUser();

  const [pools, setPools] = useState<PoolView[]>([]);
  const [advisors, setAdvisors] = useState<AdvisorView[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingAdvisorId, setPendingAdvisorId] = useState<string | null>(null);
  const [errBanner, setErrBanner] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Bounce unauthenticated visitors to the portal /login. Non-admins
  // see a friendly error banner via AdminSessionBanner + the auth gate
  // on the API endpoints (which return 403).
  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      window.location.href = redirectToPortalLogin();
    }
  }, [auth.status]);

  const refetch = useCallback(async () => {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const res = await authedFetch('/api/admin/advisors', { cache: 'no-store' });
      const json = (await res.json()) as AdvisorsApiResponse;
      if (!res.ok || !json.ok) {
        setErrBanner(json.message ?? json.error ?? `HTTP ${res.status}`);
        return;
      }
      setPools(json.pools ?? []);
      setAdvisors(json.advisors ?? []);
      setLastUpdated(new Date());
      setErrBanner(null);
    } catch (e) {
      setErrBanner(e instanceof Error ? e.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function postAction(advisorId: string, body: Record<string, unknown>): Promise<boolean> {
    if (auth.status !== 'authenticated' || !auth.user.isAdmin) return false;
    setPendingAdvisorId(advisorId);
    try {
      const res = await authedFetch('/api/admin/advisor-action', {
        method: 'POST',
        body: JSON.stringify({ advisorId, ...body }),
      });
      const json = (await res.json()) as ActionApiResponse;
      if (!res.ok || !json.ok) {
        setErrBanner(json.message ?? json.error ?? `HTTP ${res.status}`);
        return false;
      }
      await refetch();
      return true;
    } catch (e) {
      setErrBanner(e instanceof Error ? e.message : 'Network error');
      return false;
    } finally {
      setPendingAdvisorId(null);
    }
  }

  const activeAdvisors = advisors.filter((a) => a.isActive);
  const inactiveAdvisors = advisors.filter((a) => !a.isActive);

  return (
    <div className="admin-app">
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1 className="admin-title">Advisor &amp; Pool Management</h1>
            <p className="admin-sub">
              Toggle advisors on / off, assign to MPS pools, adjust round-robin
              weights and ordering
              {lastUpdated && (
                <>
                  {' · last updated '}
                  <strong>
                    {new Intl.DateTimeFormat('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                    }).format(lastUpdated)}
                  </strong>
                </>
              )}
            </p>
          </div>
        </div>
        <nav className="admin-nav">
          <a
            href="/admin/routing"
            className="admin-nav-link"
          >
            Routing Audit Log
          </a>
          <a
            href="/admin/advisors"
            className="admin-nav-link admin-nav-link-active"
          >
            Advisors &amp; Pools
          </a>
          <a
            href="/admin/availability"
            className="admin-nav-link"
          >
            Advisor Availability
          </a>
          <a href="/admin/reminder-opt-outs" className="admin-nav-link">
            Reminder Opt-Outs
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {errBanner && (
          <div className="admin-err" role="alert">
            <strong>Error:</strong> {errBanner}{' '}
            <button
              type="button"
              className="link-btn"
              onClick={() => setErrBanner(null)}
            >
              dismiss
            </button>
          </div>
        )}

        {loading && advisors.length === 0 && !errBanner && (
          <p className="admin-empty">Loading…</p>
        )}

        {advisors.length > 0 && (
          <>
            <h2 className="admin-section-title">
              Active advisors{' '}
              <span className="admin-section-count">({activeAdvisors.length})</span>
            </h2>
            <div className="admin-advisor-grid">
              {activeAdvisors.map((a) => (
                <AdvisorCard
                  key={a.id}
                  advisor={a}
                  pools={pools}
                  pending={pendingAdvisorId === a.id}
                  onAction={(body) => postAction(a.id, body)}
                />
              ))}
            </div>

            <h2 className="admin-section-title admin-section-title-paused">
              Paused advisors{' '}
              <span className="admin-section-count">
                ({inactiveAdvisors.length})
              </span>
            </h2>
            <p className="admin-section-help">
              These advisors are excluded from all routing. Toggle Active to bring
              them back online; you'll need to assign them to pools afterward.
            </p>
            <div className="admin-advisor-grid">
              {inactiveAdvisors.map((a) => (
                <AdvisorCard
                  key={a.id}
                  advisor={a}
                  pools={pools}
                  pending={pendingAdvisorId === a.id}
                  onAction={(body) => postAction(a.id, body)}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

interface AdvisorCardProps {
  advisor: AdvisorView;
  pools: PoolView[];
  pending: boolean;
  onAction: (body: Record<string, unknown>) => Promise<boolean>;
}

function AdvisorCard({ advisor, pools, pending, onAction }: AdvisorCardProps) {
  const membershipBySlug = useMemo(() => {
    const m = new Map<string, MembershipView>();
    for (const mem of advisor.memberships) m.set(mem.poolSlug, mem);
    return m;
  }, [advisor.memberships]);

  return (
    <section
      className={`advisor-card ${
        advisor.isActive ? 'advisor-card-active' : 'advisor-card-paused'
      } ${pending ? 'advisor-card-pending' : ''}`}
    >
      <header className="advisor-card-header">
        <div>
          <h3 className="advisor-card-name">{advisor.fullName}</h3>
          <p className="advisor-card-email">{advisor.email}</p>
          {advisor.notes && <p className="advisor-card-notes">{advisor.notes}</p>}
        </div>
        <div className="advisor-card-toggles">
          <button
            type="button"
            className={`advisor-toggle ${
              advisor.isActive ? 'advisor-toggle-on' : 'advisor-toggle-off'
            }`}
            disabled={pending}
            onClick={() =>
              onAction({ action: 'set_active', value: !advisor.isActive })
            }
            aria-label={advisor.isActive ? 'Set inactive' : 'Set active'}
          >
            <span className="advisor-toggle-knob" />
            <span className="advisor-toggle-label">
              {advisor.isActive ? 'ACTIVE' : 'PAUSED'}
            </span>
          </button>
          {/* V5 lead router (Phase 2.5): per-advisor Yes-Leads opt-in.
              When ON, this advisor is eligible for routing when the
              inbound Kartra payload's consent fields ("Contact You" or
              "Would You Like To Be Contacted") are set to 'Yes'. The
              scheduler pools ARE the lead pools — same pool_membership
              rows — so this is the only Leads-specific control on the
              card. */}
          <label
            className={`advisor-yes-toggle ${
              advisor.receivesYesLeads ? 'advisor-yes-on' : 'advisor-yes-off'
            } ${pending ? 'advisor-yes-pending' : ''}`}
            title="When ON, this advisor also receives Yes Leads from Kartra (consent-field opt-in)."
          >
            <input
              type="checkbox"
              checked={advisor.receivesYesLeads}
              disabled={pending}
              onChange={() =>
                onAction({
                  action: 'set_receives_yes_leads',
                  value: !advisor.receivesYesLeads,
                })
              }
            />
            <span className="advisor-yes-label">Receives Yes Leads</span>
          </label>
          {/* Master lead-router toggle (8/17/26): when OFF this
              advisor receives NO Kartra leads at all — initial
              routing and SLA reassignment both skip them. The
              appointment booking flow is unaffected. */}
          <label
            className={`advisor-yes-toggle ${
              advisor.receivesLeads ? 'advisor-yes-on' : 'advisor-yes-off'
            } ${pending ? 'advisor-yes-pending' : ''}`}
            title="When ON, this advisor receives new Kartra leads (all leads, not just Yes Leads). Turn OFF to route new leads around them entirely."
          >
            <input
              type="checkbox"
              checked={advisor.receivesLeads}
              disabled={pending}
              onChange={() =>
                onAction({
                  action: 'set_receives_leads',
                  value: !advisor.receivesLeads,
                })
              }
            />
            <span className="advisor-yes-label">Receives New Leads</span>
          </label>
        </div>
      </header>

      <table className="pool-table">
        <thead>
          <tr>
            <th>Pool</th>
            <th>Member</th>
            <th>Weight</th>
            <th>Level</th>
            <th>This month</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pools.map((pool) => {
            const mem = membershipBySlug.get(pool.slug);
            const isMember = !!mem;
            return (
              <tr key={pool.id} className={isMember ? 'pool-row-member' : ''}>
                <td className="pool-cell-name">{pool.displayName}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={isMember}
                    disabled={pending}
                    onChange={() => {
                      onAction({
                        action: isMember ? 'remove_from_pool' : 'add_to_pool',
                        poolSlug: pool.slug,
                      });
                    }}
                  />
                </td>
                <td>
                  {isMember ? (
                    <WeightInput
                      initial={mem.weight}
                      disabled={pending}
                      onCommit={(weight) =>
                        onAction({
                          action: 'set_weight',
                          poolSlug: pool.slug,
                          weight,
                        })
                      }
                    />
                  ) : (
                    <span className="pool-cell-muted">—</span>
                  )}
                </td>
                <td className="pool-cell-num">
                  {isMember ? mem.currentLevel : '—'}
                </td>
                <td className="pool-cell-num">
                  {isMember
                    ? `${mem.meetingsCountThisMonth} / ${mem.meetingsCountTotal}`
                    : '—'}
                </td>
                <td>
                  {isMember && (
                    <button
                      type="button"
                      className="pool-bump-btn"
                      disabled={pending}
                      onClick={() =>
                        onAction({
                          action: 'bump_to_next',
                          poolSlug: pool.slug,
                        })
                      }
                      title="Make this advisor next in the round-robin for this pool"
                    >
                      Bump next
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

interface WeightInputProps {
  initial: number;
  disabled: boolean;
  onCommit: (weight: number) => void;
}

/**
 * Weight input — saves on blur or Enter. Doesn't fire on every keystroke
 * so we don't hammer the API.
 */
function WeightInput({ initial, disabled, onCommit }: WeightInputProps) {
  const [val, setVal] = useState(String(initial));

  // Reset when prop changes (after refetch).
  useEffect(() => {
    setVal(String(initial));
  }, [initial]);

  function commit() {
    const n = Number(val);
    if (!Number.isFinite(n)) {
      setVal(String(initial));
      return;
    }
    const clamped = Math.max(0, Math.min(100, Math.round(n)));
    if (clamped !== initial) {
      onCommit(clamped);
    } else {
      setVal(String(clamped));
    }
  }

  return (
    <input
      type="number"
      min={0}
      max={100}
      value={val}
      disabled={disabled}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      }}
      className="weight-input"
    />
  );
}
